
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { ResellerPlanDetails as PlanDetails } from '@/hooks/license/useResellerPlans';
import { Badge } from '@/components/ui/badge';

interface ResellerPlanDetailsComponentProps {
  resellerPlan: PlanDetails;
}

const ResellerPlanDetailsComponent: React.FC<ResellerPlanDetailsComponentProps> = ({
  resellerPlan
}) => {
  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row justify-between items-start">
        <div>
          <CardTitle>Reseller Plan Details</CardTitle>
          <CardDescription>
            Review your reseller plan features and details
          </CardDescription>
        </div>
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          Master Synced Plan
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
          <p className="text-blue-800">
            Your reseller plan is managed at the Master License level. You can view but not modify these settings.
          </p>
        </div>
        
        {resellerPlan ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-medium mb-2">Plan Information</h3>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Plan Name</TableCell>
                    <TableCell>{resellerPlan.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Description</TableCell>
                    <TableCell>{resellerPlan.description}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Annual Price</TableCell>
                    <TableCell>${resellerPlan.annualPrice || 999}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Setup Fee</TableCell>
                    <TableCell>${resellerPlan.setupFee}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Admin Limit</TableCell>
                    <TableCell>{resellerPlan.maxAdmins}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">User Limit</TableCell>
                    <TableCell>{resellerPlan.maxUsers}</TableCell>
                  </TableRow>
                  {resellerPlan.planNumber && (
                    <TableRow>
                      <TableCell className="font-medium">Plan Number</TableCell>
                      <TableCell>{resellerPlan.planNumber}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-2">Included Features</h3>
              <ul className="space-y-2">
                {resellerPlan.features
                  .filter(feature => feature.included)
                  .map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className={feature.highlight ? 'font-medium text-blue-700' : ''}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p>No reseller plan information available.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ResellerPlanDetailsComponent;
